<template>
    <div class="h-100">
        <div class="h-100" v-if="feedEnabled">
            <grid
                ref="grid"
                v-if="feed.length > 0"
                grid-theme="ag-theme-alpine-partner"
                row-selection="multiple"
                :row-data="feed"
                :suppress-row-click-selection="true"
                :column-defs="columnDefs"
                :header-height="50"
                :is-auto-width="true"
                @selection-changed="handleSelectionChange"
            >
                <responsive-dropdown v-if="!hasSelectedRows" variant="info" :items="channelItems" class="mb-1 mb-md-0">
                    {{ activeChannelLabel }}
                </responsive-dropdown>
                <div class="w-100 m-0 d-flex d-sm-none"></div>
                <responsive-button
                    v-if="!hasSelectedRows"
                    :icon="['fas', 'circle-info']"
                    :responsiveness="false"
                    variant="primary"
                    @click="handleInfoClick"
                ></responsive-button>
                <responsive-button
                    v-if="!hasSelectedRows"
                    :icon="['fas', 'gear']"
                    @click="handleSettingsClick"
                    variant="primary"
                    >Settings</responsive-button
                >
                <responsive-dropdown
                    v-if="!hasSelectedRows && ![NEXTPAX_CHANNEL_BOO142, NEXTPAX_CHANNEL_EXP270].includes(activeChannel)"
                    :icon="['fas', 'recycle']"
                    :items="channelOptions"
                    variant="primary"
                    >{{ reuseFeedLabel }}
                </responsive-dropdown>
                <responsive-button
                    v-if="!hasSelectedRows"
                    :icon="['fas', 'arrows-rotate']"
                    variant="primary"
                    @click="handleRefreshClick"
                    >Refresh</responsive-button
                >
                <b-button-group v-if="!hasSelectedRows" class="toggle-feed d-none d-sm-flex">
                    <form-square-switch
                        :labels="{
                            checked: 'Feed On',
                            unchecked: 'Feed Off',
                        }"
                        :value="feedEnabled"
                        @change="onChangeFeedEnabled"
                        :isToolbar="true"
                        :sync="true"
                        :disabled="!hasActiveCardSubscription"
                    />
                </b-button-group>
                <b-button-group v-if="!hasSelectedRows" class="d-sm-none d-flex">
                    <more-filters-dropdown class="nextpax-dropdown">
                        <form-square-switch
                            v-if="!hasSelectedRows"
                            :labels="{
                                checked: 'Feed On',
                                unchecked: 'Feed Off',
                            }"
                            :value="feedEnabled"
                            @change="onChangeFeedEnabled"
                            class="toggle-feed mr-1 w-100"
                            :isToolbar="true"
                            :sync="true"
                            :disabled="!hasActiveCardSubscription"
                        />
                    </more-filters-dropdown>
                </b-button-group>
                <bulk-actions
                    v-if="hasSelectedRows"
                    :selected-count="currentSelectedRows.length"
                    :bulk-action-defs="bulkActionDefs"
                />
                <settings-modal-form
                    :key="settingsModalKey"
                    :modal-id="settingsModalId"
                    :partner-settings="partnerSettings"
                    :active-channel="activeChannel"
                    @success-save="handleSettingsSaveSuccess"
                    @hidden="handleSettingsHidden"
                />
            </grid>
        </div>
        <b-col v-if="allowNextpax">
            <b-row v-if="!feedEnabled && !isLoading && !busy" class="mt-1 ml-2">
                <b-col>
                    <b-row>
                        <responsive-dropdown variant="info" :items="channelItems" class="mb-1">
                            {{ activeChannelLabel }}
                        </responsive-dropdown>
                        <div class="w-100 m-0 d-flex d-sm-none"></div>
                        <responsive-button
                            :icon="['fas', 'circle-info']"
                            variant="primary"
                            class="mb-1"
                            @click="handleInfoClick"
                        ></responsive-button>
                        <form-square-switch
                            :labels="{
                                checked: 'Feed On',
                                unchecked: 'Feed Off',
                            }"
                            :value="feedEnabled"
                            @change="onChangeFeedEnabled"
                            class="toggle-feed mb-1 mr-1"
                            :isToolbar="true"
                            :sync="true"
                            :disabled="!hasActiveCardSubscription"
                        />
                        <div v-if="!hasActiveCardSubscription" class="align-self-center mb-1">
                            <span class="text-danger">
                                An Active Subscription is required for enabling
                                {{ activeChannelLabel }}
                                feed.
                            </span>
                        </div>
                    </b-row>
                </b-col>
            </b-row>
        </b-col>
        <b-col v-if="!allowNextpax && allowNextpaxRequest" class="mt-1">
            <b-row v-if="!hasActiveCardSubscription">
                <b-col cols="5">
                    <span class="text-danger"> An Active Subscription is required for enabling Nextpax. </span>
                </b-col>
            </b-row>
            <b-row class="mt-3 mt-1">
                <b-col sm="12" md="5">
                    In addition to any each Nextpax subchannel host fees, Lodgix will charge 1% on all bookings (rental
                    charges only) originating from Nextpax. This billing is done once per month, and assessed on any
                    Nextpax bookings that were completed in the previous month. Most will pass all host fees through to
                    the guest using a rate multiplier.
                </b-col>
            </b-row>
            <b-row class="mt-3">
                <b-col>
                    <responsive-button
                        :icon="['fas', 'right-to-bracket']"
                        :responsiveness="false"
                        @click="handleRequestNextpaxAccessClick"
                        variant="primary"
                        :disabled="!hasActiveCardSubscription"
                        >Request Nextpax Publish Access</responsive-button
                    >
                </b-col>
            </b-row>
        </b-col>
        <b-col v-if="!allowNextpax && !allowNextpaxRequest && !busy" class="mt-1">
            <b-row class="mt-3 mt-1">
                <b-col sm="12" md="5">
                     Nextpax Publish Request sent. Please come back later to see if your request has been approved.
                </b-col>
            </b-row>
        </b-col>            
        <modal :id="infoModalId" hide-header ok-only>
            <span v-if="activeChannel == NEXTPAX_CHANNEL_BOO142">
                <b-alert show variant="info">
                    Each Lodgix property requires a separate booking.com property id. Incremental availability and rate
                    data are updated on Booking.com every five (5) minutes.
                </b-alert>
                <div class="mb-3">
                    <a href="https://support.lodgix.com/en/articles/3323784-booking-com-integration" target="_blank"
                        >READ THE BOOKING.COM SETUP DOCUMENTATION</a
                    >
                </div></span
            >
            <b-alert v-if="activeChannel == NEXTPAX_CHANNEL_EXP270" variant="info" show>
                Each Lodgix property requires a separate Expedia property ID, rate plan ID and room type ID.
            </b-alert>
            <b-alert show variant="info" v-if="!feedEnabled">
                <div>
                    Unless {{ activeChannelLabel }} is collecting guest payments, a payment gateway is required to store
                    guest credit card data sent by {{ activeChannelLabel }}.<br />
                </div>
                Failure to setup a payment gateway before initiating the
                {{ activeChannelLabel }} connection, may result in service disruptions if {{ activeChannelLabel }} is
                not collecting guest payments.
            </b-alert>
            <div v-if="feedEnabled">
                <div>Feed enabled on: {{ feedEnabledOn }}</div>
                <a href="#" @click="handleAgreementClick">Terms and conditions</a>
                agreed on: {{ feedEnabledOn }}
            </div>
        </modal>
        <modal :id="agreementModalId" :title="activeChannelLabel + ' Agreement'" ok-only size="xl">
            <terms-and-conditions :channel="activeChannelLabel" />
        </modal>
        <modal
            :id="requestNextpaxAccessModalId"
            title="Request Nextpax Publish Access"
            ok-title="Request Access"
            @ok="requestNextpaxAccess"
            :ok-disabled="busy"
        >
            <span
                >Requesting access is required to publish properties to Nextpax. Click the "Request Access" button below
                and someone from Lodgix will review your request. Please come back later to see if your request has been
                approved.</span
            >
        </modal>
    </div>
</template>

<script>
import API from '../../../api';
import isNil from 'lodash/isNil';
import range from 'lodash/range';
import {
    PARTNER_NEXTPAX_FEED_SHARE_BULK_PROPERTY_ENABLE_ENDPOINT,
    PARTNER_NEXTPAX_FEED_SHARE_BULK_PROPERTY_DISABLE_ENDPOINT,
    PARTNER_NEXTPAX_FEED_SHARE_BY_ID_ENDPOINT,
    PARTNER_NEXTPAX_FEED_SHARE_FEED_ENDPOINT,
    PARTNER_NEXTPAX_DATA_ENDPOINT,
    PARTNER_NEXTPAX_SETTINGS_ENDPOINT,
    PARTNER_NEXTPAX_REQUEST_ACCESS_ENDPOINT,
} from '../../../endpoints';
import {
    NEXTPAX_CHANNEL_BOO142,
    NEXTPAX_CHANNEL_EXP270,
    NEXTPAX_CHANNEL_MAPPING,
    getNextpaxChannels,
} from '../../../constants';
import { mapGetters } from 'vuex';
import { formatResponseError } from '../../../utils/api';
import { isMediumScreen } from '../../../utils/screens';
import { toAmountDisplay } from '../../../utils/numbers';
import { escapeHTML } from '../../../utils/strings';
import Grid from '../../generic/grid/grid.vue';
import Modal from '../../generic/modal/modal.vue';
import BulkActions from '../../generic/bulk-actions/bulk-actions.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
import FormSquareSwitch from '../../generic/form-square-switch/form-square-switch.vue';
import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
import SettingsModalForm from './settings-modal-form.vue';
import TermsAndConditions from './terms-and-conditions.vue';

export default {
    name: 'PartnerNextpaxChannel',
    components: {
        Grid,
        Modal,
        BulkActions,
        ResponsiveButton,
        ResponsiveDropdown,
        FormSquareSwitch,
        MoreFiltersDropdown,
        SettingsModalForm,
        TermsAndConditions,
    },
    props: {
        activeChannel: String,
    },
    mounted() {
        this.load();
    },
    computed: {
        ...mapGetters('header', ['isLoading']),
        hasSelectedRows() {
            return this.currentSelectedRows.length > 0;
        },
        reuseFeedLabel() {
            return this.nextpaxChannelReuse !== ''
                ? `Reuse ${NEXTPAX_CHANNEL_MAPPING[this.nextpaxChannelReuse]} Feed`
                : 'Not Reuse Feed';
        },
        channelOptions() {
            return [
                {
                    label: 'Not Reuse Feed',
                    active: this.nextpaxChannelReuse === '',
                    click: () => this.reuseFeed(''),
                },
            ].concat(
                getNextpaxChannels()
                    .filter((channel) => channel !== this.activeChannel)
                    .map((channel) => ({
                        label: `Reuse ${NEXTPAX_CHANNEL_MAPPING[channel]} Feed`,
                        active: channel === this.nextpaxChannelReuse,
                        click: () => this.reuseFeed(channel),
                    }))
            );
        },
        channelItems() {
            return getNextpaxChannels().map((channel) => ({
                icon: this.enabledChannels.has(channel) ? ['fas', 'circle-check'] : ['far', 'circle'],
                label: NEXTPAX_CHANNEL_MAPPING[channel],
                active: channel === this.activeChannel,
                click: () => {
                    this.$parent.$emit('active-channel-change', channel);
                },
            }));
        },
        activeChannelLabel() {
            return NEXTPAX_CHANNEL_MAPPING[this.activeChannel];
        },
        activeChannelHeaderName() {
            if (this.activeChannel === 'BOO142') {
                return 'Booking.com Hotel';
            }
            return this.activeChannelLabel;
        },
        isMobile() {
            const md = 768;
            return window.innerWidth < md || window.innerHeight < md;
        },
        columnDefs() {
            return [
                {
                    field: 'rental_property.id',
                    headerName: 'P. ID',
                    minWidth: 60,
                    pinned: 'left',
                    headerCheckboxSelection: this.nextpaxChannelReuse === '',
                    headerCheckboxSelectionFilteredOnly: true,
                    checkboxSelection: this.nextpaxChannelReuse === '',
                },
                {
                    field: 'rental_property.name',
                    headerName: 'P. Name',
                    suppressSizeToFit: true,
                    pinned: 'left',
                    minWidth: 80,
                    width: isMediumScreen() ? undefined : 80,
                    autoWidthExclude: this.isMobile,
                    cellRenderer: ({ data }) => {
                        if (!data) {
                            return;
                        }
                        const id = data.remote_id;
                        const name = escapeHTML(data.rental_property.name);
                        const href =
                            this.activeChannel == NEXTPAX_CHANNEL_BOO142
                                ? `https://www.booking.com/searchresults.en-gb.html?ss=Hotelid%3A${id}`
                                : `https://www.expedia.com/a.h${data.remote_id}.Hotelinfo`;
                        if (
                            data.remote_id != null &&
                            [NEXTPAX_CHANNEL_BOO142, NEXTPAX_CHANNEL_EXP270].includes(this.activeChannel)
                        ) {
                            return `<a target="_blank" href="${href}">${name}</a>`;
                        }
                        return `<span title="${name}">${name}<span>`;
                    },
                },
                {
                    field: 'rate_multiplier',
                    headerName: 'Rate Multiplier',

                    type: this.nextpaxChannelReuse === '' ? 'editableColumn' : '',
                    cellClass:
                        this.nextpaxChannelReuse === ''
                            ? 'lx-editable-cell d-flex justify-content-center text-primary'
                            : 'd-flex justify-content-center',
                    headerClass: 'ag-centered-header',
                    cellEditorParams: {
                        isDecimal: true,
                        select: true,
                        empty: true,
                        emptyValue: null,
                        maxDecimalPlaces: 4,
                    },
                    sortable: false,
                    minWidth: 80,
                    valueParser: (value) => value.rate_multiplier,

                    cellRenderer: ({ value }) => {
                        if (isNil(value)) {
                            return 'Not set';
                        }
                        return toAmountDisplay({
                            amount: value,
                            decimalPlaces: 4,
                        });
                    },
                    valueSetter: async ({ node, data, newValue, oldValue }) => {
                        (async () => {
                            try {
                                await API.patch(PARTNER_NEXTPAX_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                    rate_multiplier: newValue,
                                });
                            } catch (e) {
                                this.fieldBasedErrorToast({
                                    error: e,
                                    field: 'rate_multiplier',
                                    defaultText: 'Could not save multiplier.',
                                });

                                data.rate_multiplier = oldValue;
                                node.setData(data);
                            }
                        })();
                        data.rate_multiplier = newValue;
                        return true;
                    },
                },
                {
                    field: 'rate_multiplier_currency',
                    headerName: 'Rate Multiplier Currency',
                    cellClass: this.nextpaxChannelReuse === '' ? 'lx-editable-cell text-primary' : '',
                    cellEditor: this.nextpaxChannelReuse === '' ? 'selectCellEditor' : '',
                    editable: this.nextpaxChannelReuse === '' ? true : false,
                    minWidth: 75,
                    valueParser: (value) => value.rate_multiplier_currency,

                    cellRenderer: ({ data }) =>
                        data && this.getRateMultiplierCurrencyText(data.rate_multiplier_currency),
                    cellEditorParams: () => ({
                        options: this.currencyOptions,
                    }),
                    valueSetter: async ({ node, data, newValue, oldValue }) => {
                        (async () => {
                            try {
                                await API.patch(PARTNER_NEXTPAX_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                    rate_multiplier_currency: newValue,
                                });
                            } catch (e) {
                                this.fieldBasedErrorToast({
                                    error: e,
                                    field: 'rate_multiplier_currency',
                                    defaultText: 'Could not save rate multiplier currency.',
                                });

                                data.rate_multiplier_currency = oldValue;
                                node.setData(data);
                            }
                        })();
                        data.rate_multiplier_currency = newValue;
                        return true;
                    },
                },
                {
                    field: 'remote_id',
                    headerName: `${this.activeChannelHeaderName} ID`,
                    type: 'editableColumn',
                    cellClass: 'lx-editable-cell text-primary',
                    cellEditorParams: {
                        select: true,
                        empty: true,
                        emptyValue: null,
                    },
                    minWidth: 65,
                    cellRenderer: ({ value }) => {
                        if (isNil(value)) {
                            return 'Not assigned';
                        }
                        return value;
                    },
                    valueParser: (value) => value.remote_id,

                    valueSetter: async ({ node, data, newValue, oldValue }) => {
                        (async () => {
                            try {
                                await API.patch(PARTNER_NEXTPAX_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                    remote_id: newValue,
                                });
                                this.refetchFeed();
                            } catch (e) {
                                this.fieldBasedErrorToast({
                                    error: e,
                                    field: 'remote_id',
                                    defaultText: 'Could not save remote id.',
                                });
                                data.remote_id = oldValue;
                                node.setData(data);
                            }
                        })();
                        data.remote_id = newValue;
                        return true;
                    },
                    hide: ![NEXTPAX_CHANNEL_BOO142, NEXTPAX_CHANNEL_EXP270].includes(this.activeChannel),
                },
                {
                    field: 'room_type_id',
                    headerName: 'Room Type ID',
                    type: 'editableColumn',
                    cellClass: 'lx-editable-cell text-primary',
                    cellEditorParams: {
                        isNumeric: true,
                        select: true,
                        empty: true,
                        emptyValue: null,
                    },
                    minWidth: 65,
                    cellRenderer: ({ value }) => {
                        if (isNil(value)) {
                            return 'Not assigned';
                        }
                        return value;
                    },
                    valueParser: (value) => value.room_type_id,
                    valueSetter: async ({ node, data, newValue, oldValue }) => {
                        (async () => {
                            try {
                                await API.patch(PARTNER_NEXTPAX_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                    room_type_id: newValue,
                                });
                            } catch (e) {
                                this.fieldBasedErrorToast({
                                    error: e,
                                    field: 'room_type_id',
                                    defaultText: 'Could not save room type id.',
                                });
                                data.room_type_id = oldValue;
                                node.setData(data);
                            }
                        })();
                        data.room_type_id = newValue;
                        return true;
                    },
                    hide: ![NEXTPAX_CHANNEL_BOO142, NEXTPAX_CHANNEL_EXP270].includes(this.activeChannel),
                },
                {
                    field: 'rate_plan_id',
                    headerName: 'Rate Plan ID',
                    type: 'editableColumn',
                    cellClass: 'lx-editable-cell text-primary',
                    cellEditorParams: {
                        isNumeric: true,
                        select: true,
                        empty: true,
                        emptyValue: null,
                    },
                    minWidth: 65,
                    cellRenderer: ({ value }) => {
                        if (isNil(value)) {
                            return 'Not assigned';
                        }
                        return value;
                    },
                    valueParser: (value) => value.rate_plan_id,
                    valueSetter: async ({ node, data, newValue, oldValue }) => {
                        (async () => {
                            try {
                                await API.patch(PARTNER_NEXTPAX_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                    rate_plan_id: newValue,
                                });
                            } catch (e) {
                                this.fieldBasedErrorToast({
                                    error: e,
                                    field: 'rate_plan_id',
                                    defaultText: 'Could not save rate plan id.',
                                });
                                data.rate_plan_id = oldValue;
                                node.setData(data);
                            }
                        })();
                        data.rate_plan_id = newValue;
                        return true;
                    },
                    hide: ![NEXTPAX_CHANNEL_BOO142, NEXTPAX_CHANNEL_EXP270].includes(this.activeChannel),
                },
                {
                    field: 'extra_person_base',
                    headerName: 'Base Guests',
                    cellClass: 'lx-editable-cell ag-right-aligned-cell text-primary',
                    headerClass: 'ag-right-aligned-header',
                    cellEditor: 'selectCellEditor',
                    editable: true,
                    minWidth: 75,
                    cellRenderer: ({ data }) => (!!data ? data.extra_person_base || 'Disabled' : ''),
                    cellEditorParams: () => ({
                        options: this.extraPersonBaseOptions,
                    }),
                    valueSetter: async ({ node, data, newValue, oldValue }) => {
                        (async () => {
                            try {
                                await API.patch(PARTNER_NEXTPAX_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                    extra_person_base: newValue,
                                });
                                this.refetchFeed();
                            } catch (e) {
                                this.fieldBasedErrorToast({
                                    error: e,
                                    field: 'extra_person_base',
                                    defaultText: 'Could not save base guests.',
                                });
                                data.extra_person_base = oldValue;
                                node.setData(data);
                            }
                        })();
                        data.extra_person_base = newValue;
                        return true;
                    },
                    hide: this.activeChannel !== NEXTPAX_CHANNEL_BOO142,
                },
                {
                    field: 'extra_person_rate',
                    headerName: 'Addtl Guest Rate',
                    type: 'editableColumn',
                    cellClass: ({ data }) =>
                        data.extra_person_base
                            ? 'lx-editable-cell ag-right-aligned-cell text-primary'
                            : 'ag-right-aligned-cell',
                    headerClass: 'ag-right-aligned-header',
                    cellEditorParams: {
                        isNumeric: true,
                        select: true,
                        empty: true,
                        emptyValue: 0,
                    },
                    valueParser: (value) => value.extra_person_base,
                    minWidth: 65,
                    cellRenderer: ({ data, value }) => {
                        if (!data.extra_person_base) {
                            return 'Not set';
                        }
                        return toAmountDisplay({
                            amount: value,
                            integer: true,
                        });
                    },
                    editable: ({ data }) => !!data.extra_person_base,
                    valueSetter: async ({ node, data, newValue, oldValue }) => {
                        (async () => {
                            try {
                                if (newValue === null) newValue = 0;
                                await API.patch(PARTNER_NEXTPAX_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                    extra_person_rate: newValue,
                                });
                            } catch (e) {
                                this.fieldBasedErrorToast({
                                    error: e,
                                    field: 'extra_person_rate',
                                    defaultText: 'Could not save additional guest rate.',
                                });
                                data.extra_person_rate = oldValue;
                                node.setData(data);
                            }
                        })();
                        data.extra_person_rate = newValue;
                        return true;
                    },
                    hide: this.activeChannel !== NEXTPAX_CHANNEL_BOO142,
                },
                {
                    field: 'serving_status',
                    headerName: 'Status',
                },
                {
                    field: 'enabled',
                    type: this.nextpaxChannelReuse === '' ? 'switchColumn' : 'yesNoColumnNew',
                    headerName: 'Enabled',
                    minWidth: 85,
                    sortable: false,
                    resizable: false,
                    autoWidthExclude: true,
                    cellRendererParams: {
                        onChange: async ({ data, node, api }, value) => {
                            try {
                                await API.patch(PARTNER_NEXTPAX_FEED_SHARE_BY_ID_ENDPOINT(data.id), {
                                    enabled: value,
                                });
                            } catch (e) {
                                this.fieldBasedErrorToast({
                                    error: e,
                                    field: 'enabled',
                                    defaultText: 'Could not save enabled.',
                                });

                                api.redrawRows({ rowNodes: [node] });
                                return;
                            }
                        },
                    },
                },
            ];
        },
    },
    data() {
        const defaultChannels = getNextpaxChannels();
        return {
            busy: false,
            feedEnabled: false,
            feedEnabledOn: null,
            partnerSettings: null,
            hasActiveCardSubscription: false,
            settingsModalId: 'nextpax-settings-modal',
            settingsModalKey: 1000,
            agreementModalId: 'agreement-modal',
            infoModalId: 'info-modal',
            localStorageActiveNextpaxChannelKey: 'partnerActiveNextpaxChannel',
            nextpaxChannelReuse: '',
            allowNextpax: false,
            allowNextpaxRequest: false,
            NEXTPAX_CHANNEL_BOO142: NEXTPAX_CHANNEL_BOO142,
            NEXTPAX_CHANNEL_EXP270: NEXTPAX_CHANNEL_EXP270,
            CHANNELS: defaultChannels,
            enabledChannels: new Set(),
            currentSelectedRows: [],
            currencyOptions: [],
            feed: [],
            bulkActionDefs: [
                {
                    label: 'Enable',
                    group: 'Property',
                    onClick: this.handlePropertyBulkEnable,
                },
                {
                    label: 'Disable',
                    group: 'Property',
                    onClick: this.handlePropertyBulkDisable,
                },
            ],
            requestNextpaxAccessModalId: 'request-nextpax-access-modal',
        };
    },
    methods: {
        extraPersonBaseOptions() {
            return [{ value: 0, text: 'Disabled' }, ...range(1, 100).map((n) => ({ value: n, text: n }))];
        },
        getRateMultiplierCurrencyText(currency) {
            return this.currencyOptions.find((c) => c.value === currency).text;
        },
        handleSelectionChange() {
            const gridApi = this.$refs.grid.gridOptions.api;
            this.currentSelectedRows = gridApi.getSelectedRows();
            this.currentFeedShare = null;
        },
        fieldBasedErrorToast({ error, field, defaultText }) {
            const { generic, fields } = formatResponseError(error);
            const errorText = fields[field] && !!fields[field].length ? fields[field][0] : generic;
            this.errorToast({
                text: errorText || defaultText,
            });
        },
        handleRefreshClick() {
            this.refetchFeed();
        },
        async refetchFeed() {
            this.$parent.$emit('busy', true);
            try {
                await Promise.all([this.fetchFeed(), this.fetchPartnerSettings()]);
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
        async fetchFeed() {
            this.currentSelectedRows = [];
            try {
                const { data } = await API.get(PARTNER_NEXTPAX_FEED_SHARE_FEED_ENDPOINT(this.activeChannel));
                this.feed = data;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || `Could not load ${NEXTPAX_CHANNEL_MAPPING[this.activeChannel]} feed.`,
                });
            }
        },
        async requestNextpaxAccess() {
            this.busy = true;
            this.$parent.$emit('busy', true);
            try {
                await API.post(PARTNER_NEXTPAX_REQUEST_ACCESS_ENDPOINT());
                await this.load({ withBusy: false });
                this.$bvModal.hide(this.requestNextpaxAccessModalId);
                this.successToast({ text: 'Nextpax access request was sent.' });
            } catch (error) {
                const { generic } = formatResponseError(error);
                this.errorToast({
                    text: generic || 'Could not request nextpax access.',
                });
            } finally {
                this.busy = false;
                this.$parent.$emit('busy', false);
            }
        },
        handleSettingsClick() {
            this.$bvModal.show(this.settingsModalId);
        },
        handleAgreementClick() {
            this.$bvModal.show(this.agreementModalId);
        },
        handleInfoClick() {
            this.$bvModal.show(this.infoModalId);
        },
        handleRequestNextpaxAccessClick() {
            this.$bvModal.show(this.requestNextpaxAccessModalId);
        },
        handleSettingsSaveSuccess() {
            this.successToast({
                text: 'Settings were successfully saved.',
            });
            this.load();
        },
        handleSettingsHidden() {
            this.settingsModalKey++;
        },
        async fetchData() {
            try {
                const { data } = await API.get(PARTNER_NEXTPAX_DATA_ENDPOINT(this.activeChannel));
                this.hasActiveCardSubscription = data.has_active_card_subscription;
                this.currencyOptions = data.currency_options.map((c) => ({
                    value: c.id,
                    text: c.code,
                }));
                this.enabledChannels = new Set(data.enabled_channels);
                this.allowNextpax = data.allow_nextpax;
                this.allowNextpaxRequest = data.allow_nextpax_request;
            } catch {
                this.errorToast({
                    text: `Could not load ${NEXTPAX_CHANNEL_MAPPING[this.activeChannel]} data.`,
                });
            }
        },
        async fetchPartnerSettings() {
            try {
                const { data } = await API.get(PARTNER_NEXTPAX_SETTINGS_ENDPOINT(this.activeChannel));
                this.partnerSettings = data;
                this.feedEnabled = data.feed_enabled;
                this.feedEnabledOn = data.feed_enabled_on_display;
                this.nextpaxChannelReuse = data.nextpax_channel_reuse;
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || `Could not load ${NEXTPAX_CHANNEL_MAPPING[this.activeChannel]} partner settings.`,
                });
            }
        },
        async handlePropertyBulkEnable() {
            this.$parent.$emit('busy', true);
            try {
                await API.post(PARTNER_NEXTPAX_FEED_SHARE_BULK_PROPERTY_ENABLE_ENDPOINT(), {
                    nextpax_channel: this.activeChannel,
                    feed_share_ids: this.currentSelectedRows.map((row) => row.id),
                });
                await this.fetchFeed();
            } catch {
                this.errorToast({ text: 'Could not enable property.' });
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
        async handlePropertyBulkDisable() {
            this.$parent.$emit('busy', true);
            try {
                await API.post(PARTNER_NEXTPAX_FEED_SHARE_BULK_PROPERTY_DISABLE_ENDPOINT(), {
                    nextpax_channel: this.activeChannel,
                    feed_share_ids: this.currentSelectedRows.map((row) => row.id),
                });
                await this.fetchFeed();
            } catch {
                this.errorToast({ text: 'Could not disable property.' });
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
        async onChangeFeedEnabled({ value }) {
            this.$parent.$emit('busy', true);
            try {
                await API.patch(PARTNER_NEXTPAX_SETTINGS_ENDPOINT(this.activeChannel), {
                    feed_enabled: value,
                });
                this.feedEnabled = value;
                this.load();
                this.successToast({
                    text: `Feed was ${value ? 'enabled' : 'disabled'} successfully`,
                });
            } catch (e) {
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || 'Could not enable feed.',
                });
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
        async reuseFeed(nextpaxChannelReuse) {
            this.$parent.$emit('busy', true);
            try {
                this.nextpaxChannelReuse = nextpaxChannelReuse;
                await API.patch(PARTNER_NEXTPAX_SETTINGS_ENDPOINT(this.activeChannel), {
                    nextpax_channel_reuse: this.nextpaxChannelReuse,
                });
                this.nextpaxChannelReuse
                    ? this.successToast({
                          text: `Reuse was set to ${NEXTPAX_CHANNEL_MAPPING[nextpaxChannelReuse]}.`,
                      })
                    : this.successToast({
                          text: 'Reuse was disabled.',
                      });
                this.load();
            } catch (error) {
                const { generic } = formatResponseError(error);
                this.errorToast({
                    text:
                        generic || this.nextpaxChannelReuse
                            ? `Could not set reuse to ${NEXTPAX_CHANNEL_MAPPING[nextpaxChannelReuse]}.`
                            : 'Could not disable reuse.',
                });
            } finally {
                this.$parent.$emit('busy', false);
            }
        },
        async load({ withBusy = true } = {}) {
            if (withBusy) {
                this.busy = true;
                this.$parent.$emit('busy', true);
            }
            try {
                await Promise.all([this.fetchData(), this.fetchPartnerSettings()]);

                if (this.feedEnabled) {
                    await this.fetchFeed();
                }
            } finally {
                if (withBusy) {
                    this.busy = false;
                    this.$parent.$emit('busy', false);
                }
            }
        },
    },
    watch: {
        activeChannel(channel) {
            this.refetchFeed();
        },
    },
};
</script>

<style scoped lang="scss">
.nextpax-dropdown {
    :deep(.buttons-wrapper) {
        width: 100%;
    }
}
.toggle-feed {
    width: 105px;
}
.important-text {
    max-width: 30%;
    display: block;
}
</style>
